import { useCallback, useEffect } from "react";
import { Image } from "../../image";
import classnames from "classnames";
import { useImagePreviewHandlers, useImagePreviewState } from "../../image-preview-context";
import styles from "../../image.module.scss";
import { ImagePreviewImageProps } from "./image-preview-image.types";

/**
 * A component that renders an image and registers it with the nearest ImagePreview gallery.
 * Clicking this image triggers the full-screen preview modal to open at this image's index.
 *
 * @param {string} src - The URL of the image to display.
 * @param {string} alt - The alternative text for the image.
 * @param {string} className - Optional CSS class name for the image.
 * @param {React.MouseEventHandler<HTMLImageElement>} onClick - Optional click handler for the image.
 * @returns {JSX.Element} The rendered image thumbnail.
 */
export const ImagePreviewImage = ({ src, alt, className, onClick, ...rest }: ImagePreviewImageProps) => {
    const handlers = useImagePreviewHandlers();
    const { images } = useImagePreviewState();

    /**
     * Registers the image with the nearest ImagePreview gallery.
     */
    useEffect(() => {
        if (src) {
            return handlers.registerImage({ src, alt });
        }
    }, [src, alt, handlers.registerImage]);

    /**
     * Handles the click event on the image.
     * Opens the image preview modal at this image's index.
     */
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLImageElement>) => {
            onClick?.(e);
            const index = images.findIndex((img) => img.src === src);
            if (index !== -1) {
                handlers.setIndex(index);
            }
            handlers.open();
        },
        [onClick, images, src, handlers]
    );

    return (
        <Image
            {...rest}
            src={src}
            alt={alt}
            onClick={handleClick}
            className={classnames(styles.previewTrigger, className)}
        />
    );
};
